"use client";

import { useRouter, useSearchParams } from "next/navigation";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useExploreRoute } from "../_lib/explore-route-context";
import { isExploreSearchEnabled } from "../_lib/explore-search-feature";
import { applyStoryFilters } from "../story-filters";
import type {
  AllStoriesPageData,
  AllStoriesSortOption,
  ContentTypeFilter,
  ExploreBriefCard,
  ExploreStoryCard,
  FeedPageData,
  FilterOption,
  TimeRangeFilter,
} from "../types";

import { ExploreSearchInput } from "./explore-search-input";
import { FeaturedStoryCard } from "./featured-story-card";
import { FeedTimeline } from "./feed-timeline";
import { MobileFilterSheet } from "./mobile-filter-sheet";
import { MobileStoryTextCard } from "./mobile-story-text-card";
import { SearchResultsSkeleton } from "./search-results-skeleton";
import { StoriesFilterSidebar } from "./stories-filter-sidebar";
import { StoriesSearchResults } from "./stories-search-results";
import { StoriesToolbar } from "./stories-toolbar";
import { StoryGridCard } from "./story-grid-card";

// ─── Constants ───────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 20;
const FILTER_QUERY_KEYS = [
  "type",
  "category",
  "tag",
  "tags",
  "tool",
  "time",
] as const;

type FilterQueryKey = (typeof FILTER_QUERY_KEYS)[number];

/** Map UI content-type filter values to the API's MaterialType enum. */
const CONTENT_TYPE_TO_MATERIAL: Record<ContentTypeFilter, string | null> = {
  all: null,
  news: "NEWS",
  workflow: "WORKFLOW",
  release: "RELEASE",
  prompt: "PROMPT",
  deal: "DEAL",
};

/** Map UI time-range filter values to the API's `daysAgo` window. */
const TIME_RANGE_TO_DAYS: Record<TimeRangeFilter, number | null> = {
  "48h": 2,
  "7d": 7,
  "30d": 30,
  all: null,
};

// ─── Component ───────────────────────────────────────────────

const VALID_CONTENT_TYPES = new Set<ContentTypeFilter>([
  "all",
  "news",
  "workflow",
  "release",
  "prompt",
  "deal",
]);

function parseContentType(value: string | null): ContentTypeFilter {
  if (value && VALID_CONTENT_TYPES.has(value as ContentTypeFilter)) {
    return value as ContentTypeFilter;
  }
  return "all";
}

function getUrlSearchParamValue(
  params: URLSearchParams,
  key: FilterQueryKey
): string | null {
  const values: string[] = [];
  for (const item of params.getAll(key)) {
    const trimmed = item.trim();
    if (trimmed.length > 0) {
      values.push(trimmed);
    }
  }
  return values.length > 0 ? values.join(",") : null;
}

/**
 * Parse a URL slug-list param. Supports CSV (`?category=a,b`) and the
 * legacy single-slug shape (`?tag=foo`). Returns [] for null/empty so
 * `useState` initializers stay tidy.
 */
function parseSlugListParam(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const VALID_TIME_RANGES = new Set<TimeRangeFilter>(["48h", "7d", "30d", "all"]);

function parseTimeRange(value: string | null | undefined): TimeRangeFilter {
  if (value && VALID_TIME_RANGES.has(value as TimeRangeFilter)) {
    return value as TimeRangeFilter;
  }
  return "all";
}

function StoriesUrlFilterSync({
  onChange,
}: {
  onChange: (search: string) => void;
}) {
  const searchParams = useSearchParams();
  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    onChange(searchParamsKey);
  }, [onChange, searchParamsKey]);

  return null;
}

export function StoriesGridSection({
  data,
  feedData,
  briefCardStyle,
}: {
  data: AllStoriesPageData;
  feedData?: FeedPageData;
  briefCardStyle?: import("./assembly/styled-feed-timeline").BriefCardStyle;
}) {
  const router = useRouter();
  const { exploreBase, reportType } = useExploreRoute();
  const searchEnabled = isExploreSearchEnabled();

  // ── Client-side search state ────────────────────────────────
  // Search no longer round-trips through the URL/SSR. Typing in the box
  // updates `searchQuery`, which fires a debounced fetch against
  // `/api/explore/search`; the response replaces the rendered stories +
  // briefs without re-rendering the rest of the page.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchData, setSearchData] = useState<AllStoriesPageData | null>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Live region announcement string (for screen readers). Updates after
  // each completed search so AT users know the result list changed.
  const [liveRegionMessage, setLiveRegionMessage] = useState("");

  const isSearching = searchQuery.trim().length > 0;
  const effectiveData = isSearching && searchData !== null ? searchData : data;
  const { stories, filterGroups, categories } = effectiveData;

  // ── Filter state ────────────────────────────────────────────
  const [contentType, setContentType] = useState<ContentTypeFilter>("all");
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>("all");
  const [sort, setSort] = useState<AllStoriesSortOption>("newest");
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState<string[]>(
    []
  );
  const [selectedTagSlugs, setSelectedTagSlugs] = useState<string[]>([]);
  const [selectedToolSlugs, setSelectedToolSlugs] = useState<string[]>([]);

  // ── Search fetch effect ─────────────────────────────────────
  // Whenever the trimmed query OR an active facet changes, fire one
  // debounced fetch against `/api/explore/search`. The latest in-flight
  // request is aborted on change so older slow responses can't overwrite
  // a newer result.
  //
  // Facets are sent to the server (not just applied client-side) because
  // the API caps results at `limit=100`. For broad queries, a valid
  // filtered match may rank below the first 100 unfiltered hits — pushing
  // facet narrowing to the server keeps those results in the window.
  // Stable serialized keys (sorted, joined) prevent unnecessary refetches
  // when the array identity changes but the contents don't.
  const categoryParam = selectedCategorySlugs.toSorted().join(",");
  const tagParam = selectedTagSlugs.toSorted().join(",");
  const toolParam = selectedToolSlugs.toSorted().join(",");
  const searchAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!searchEnabled) {
      return;
    }
    const trimmed = searchQuery.trim();
    if (trimmed.length === 0) {
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setSearchData(null);
      setIsSearchLoading(false);
      setSearchError(null);
      setLiveRegionMessage("");
      return;
    }

    searchAbortRef.current?.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;
    setIsSearchLoading(true);
    setSearchError(null);

    const params = new URLSearchParams({
      q: trimmed,
      reportType,
      limit: "100",
    });
    const materialType = CONTENT_TYPE_TO_MATERIAL[contentType];
    if (materialType !== null) {
      params.set("materialTypes", materialType);
    }
    const days = TIME_RANGE_TO_DAYS[timeRange];
    if (days !== null) {
      params.set("daysAgo", String(days));
    }
    if (categoryParam.length > 0) {
      params.set("categorySlugs", categoryParam);
    }
    if (tagParam.length > 0) {
      params.set("tagSlugs", tagParam);
    }
    if (toolParam.length > 0) {
      params.set("toolSlugs", toolParam);
    }

    fetch(`/api/explore/search?${params.toString()}`, {
      signal: ctrl.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Search failed: ${res.status}`);
        }
        return res.json() as Promise<AllStoriesPageData>;
      })
      .then((payload) => {
        if (ctrl.signal.aborted) {
          return;
        }
        setSearchData(payload);
        setIsSearchLoading(false);
        const count = payload.search?.items.length ?? 0;
        setLiveRegionMessage(
          count === 0
            ? `No results for ${trimmed}`
            : `${count} ${count === 1 ? "result" : "results"} for ${trimmed}`
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        // Clear stale results when the active query's request fails. The
        // render branch shows the error card only when `searchData === null`,
        // so leaving prior results in place would silently keep displaying
        // matches for the previous query while the user thinks they're
        // looking at the new one.
        setSearchData(null);
        setIsSearchLoading(false);
        setSearchError("Something went wrong. Please try again.");
        setLiveRegionMessage("Search failed. Please try again.");
      });

    return () => {
      ctrl.abort();
    };
  }, [
    searchQuery,
    reportType,
    searchEnabled,
    contentType,
    timeRange,
    categoryParam,
    tagParam,
    toolParam,
  ]);

  // Resync facet state from the URL inside a tiny Suspense island, keeping
  // the story content server-readable and preserving the route's ISR cache.
  const syncFiltersFromSearchString = useCallback((search: string) => {
    const params = new URLSearchParams(search);
    setContentType(parseContentType(getUrlSearchParamValue(params, "type")));
    setTimeRange(parseTimeRange(getUrlSearchParamValue(params, "time")));
    setSelectedCategorySlugs(
      parseSlugListParam(getUrlSearchParamValue(params, "category"))
    );
    setSelectedTagSlugs(
      parseSlugListParam(
        getUrlSearchParamValue(params, "tags") ??
          getUrlSearchParamValue(params, "tag")
      )
    );
    setSelectedToolSlugs(
      parseSlugListParam(getUrlSearchParamValue(params, "tool"))
    );
  }, []);

  // ── Load More state ────────────────────────────────────────
  const [visibleCount, setVisibleCount] = useState<number>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_PAGE_SIZE;
    }
    const saved = (window.history.state as Record<string, unknown> | null)
      ?.__exploreVisibleCount;
    return typeof saved === "number" && saved > 0 ? saved : DEFAULT_PAGE_SIZE;
  });

  // ── Lookup maps (stable, built once) ────────────────────────
  const tagDisplayNames = useMemo(() => {
    const tagGroup = filterGroups.find((g) => g.key === "topic");
    const map = new Map<string, string>();
    for (const opt of tagGroup?.options ?? []) {
      map.set(opt.id, opt.label);
    }
    return map;
  }, [filterGroups]);

  const categoryDisplayNames = useMemo(() => {
    const catGroup = filterGroups.find((g) => g.key === "category");
    const map = new Map<string, string>();
    for (const opt of catGroup?.options ?? []) {
      map.set(opt.id, opt.label);
    }
    return map;
  }, [filterGroups]);

  const categoryEmojiBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of categories) {
      map.set(cat.slug, cat.emoji);
    }
    return map;
  }, [categories]);

  const toolOptions = useMemo<FilterOption[]>(() => {
    const toolGroup = filterGroups.find((g) => g.key === "product");
    return toolGroup?.options ?? [];
  }, [filterGroups]);

  // ── Brief category facets (stories + briefs combined) ──────
  // Briefs are always included in facet counts because the feed timeline
  // shows briefs regardless of type/time filters. The sidebar should match
  // what's visible in the feed.
  // In search mode `feedData` is empty (we skip the feed fetch when ?q=),
  // so source briefs from `data.briefs` instead, which holds the search
  // result briefs interleaved with stories.
  const allBriefsForCategoryFacets = useMemo(() => {
    if (isSearching && searchData) {
      return searchData.briefs ?? [];
    }
    if (!feedData) {
      return [];
    }
    return feedData.sections.flatMap((s) => s.briefs);
  }, [isSearching, searchData, feedData]);

  // ── Cascading filter pipeline ───────────────────────────────
  // Step 1: Filter by Type + Time → feeds Category facets
  const storiesAfterTypeAndTime = useMemo(() => {
    return applyStoryFilters({
      stories,
      selectedFilters: {},
      contentType,
      timeRange,
      sort,
    });
  }, [stories, contentType, timeRange, sort]);

  // Step 2: Filter by Type + Time + Category → feeds Tag + Tool facets
  const storiesAfterCategory = useMemo(() => {
    if (selectedCategorySlugs.length === 0) {
      return storiesAfterTypeAndTime;
    }
    const categorySet = new Set(selectedCategorySlugs);
    return storiesAfterTypeAndTime.filter(
      (s) => s.categorySlug && categorySet.has(s.categorySlug)
    );
  }, [storiesAfterTypeAndTime, selectedCategorySlugs]);

  // Step 2b: Briefs filtered by category (for tag/tool facets alongside stories)
  const briefsAfterCategory = useMemo<ExploreBriefCard[]>(() => {
    const allBriefs = allBriefsForCategoryFacets;
    if (selectedCategorySlugs.length === 0) {
      return allBriefs;
    }
    const categorySet = new Set(selectedCategorySlugs);
    return allBriefs.filter(
      (b) => b.categorySlug && categorySet.has(b.categorySlug)
    );
  }, [allBriefsForCategoryFacets, selectedCategorySlugs]);

  // Step 3: Full filter → displayed stories
  const filteredStories = useMemo(() => {
    let result: ExploreStoryCard[] = storiesAfterCategory;

    // Tag filter
    if (selectedTagSlugs.length > 0) {
      const tagSet = new Set(selectedTagSlugs);
      result = result.filter((s) =>
        s.allTagSlugs.some((slug) => tagSet.has(slug))
      );
    }

    // Product/tool filter
    if (selectedToolSlugs.length > 0) {
      const toolSet = new Set(selectedToolSlugs);
      result = result.filter((s) =>
        s.allTagSlugs.some((slug) => toolSet.has(slug))
      );
    }

    return result;
  }, [storiesAfterCategory, selectedTagSlugs, selectedToolSlugs]);

  // ── Has any filter active? ──────────────────────────────────
  const hasAnyFilter =
    contentType !== "all" ||
    timeRange !== "all" ||
    selectedCategorySlugs.length > 0 ||
    selectedTagSlugs.length > 0 ||
    selectedToolSlugs.length > 0;

  // ── Visible stories (Load More) ─────────────────────────────
  const clampedVisible = Math.min(visibleCount, filteredStories.length);
  const visibleStories = filteredStories.slice(0, clampedVisible);
  const hasMore = clampedVisible < filteredStories.length;

  // ── Sparse / empty fallback ───────────────────────────────
  // Cascade: remove tag/tool filters → remove category → show all
  const SPARSE_THRESHOLD = 3;
  const showFallback =
    hasAnyFilter && filteredStories.length <= SPARSE_THRESHOLD;

  const fallbackStories = useMemo(() => {
    if (!showFallback) {
      return [];
    }
    const shownIds = new Set(filteredStories.map((s) => s.storyId));
    // Try broader filter stages until we find stories
    const candidates = [storiesAfterCategory, storiesAfterTypeAndTime, stories];
    for (const pool of candidates) {
      const deduped = pool.filter((s) => !shownIds.has(s.storyId)).slice(0, 5);
      if (deduped.length > 0) {
        return deduped;
      }
    }
    return [];
  }, [
    showFallback,
    filteredStories,
    storiesAfterCategory,
    storiesAfterTypeAndTime,
    stories,
  ]);

  const fallbackLabel = useMemo(() => {
    if (selectedCategorySlugs.length > 0) {
      const names = selectedCategorySlugs
        .map((slug) => categoryDisplayNames.get(slug) ?? slug)
        .join(" & ");
      return `More in ${names}`;
    }
    return "You might also like";
  }, [selectedCategorySlugs, categoryDisplayNames]);

  // Persist visible count in history.state for back-button restoration
  useEffect(() => {
    const currentState = window.history.state as Record<string, unknown> | null;
    window.history.replaceState(
      { ...currentState, __exploreVisibleCount: visibleCount },
      ""
    );
  }, [visibleCount]);

  const activeFilterCount =
    (contentType !== "all" ? 1 : 0) +
    selectedCategorySlugs.length +
    selectedTagSlugs.length +
    selectedToolSlugs.length;

  // ── Callbacks ───────────────────────────────────────────────
  const resetVisibleCount = useCallback(
    () => setVisibleCount(DEFAULT_PAGE_SIZE),
    []
  );

  /**
   * Push facet changes to the URL whenever filters change, regardless of
   * search mode. The URL is the source of truth for facet state so that:
   *   - Search mode picks up facets server-side (composes with the query).
   *   - Non-search filter state survives a later transition into search
   *     mode (typing a query carries the existing facets along).
   *   - Filtered URLs are shareable in either mode.
   *
   * Reads the live URL at write time so back-to-back toggles don't overwrite
   * each other with a stale render-time snapshot.
   */
  const pushUrlFacets = useCallback(
    (updates: Record<string, string | null>) => {
      const liveQuery =
        typeof window === "undefined"
          ? ""
          : window.location.search.replace(/^\?/, "");
      const params = new URLSearchParams(liveQuery);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "") {
          params.delete(k);
        } else {
          params.set(k, v);
        }
      }
      const qs = params.toString();
      const currentPathname =
        typeof window === "undefined"
          ? `${exploreBase}/stories`
          : window.location.pathname;
      router.replace(qs ? `${currentPathname}?${qs}` : currentPathname, {
        scroll: false,
      });
    },
    [exploreBase, router]
  );

  const handleContentTypeChange = useCallback(
    (value: ContentTypeFilter) => {
      setContentType(value);
      pushUrlFacets({ type: value === "all" ? null : value });
      resetVisibleCount();
    },
    [pushUrlFacets, resetVisibleCount]
  );

  const handleTimeRangeChange = useCallback(
    (value: TimeRangeFilter) => {
      setTimeRange(value);
      // Push ?time= so the search service can apply the window inside each
      // per-list CTE (without it, time-narrowing happens only on the
      // already-truncated client list and would miss matches outside the
      // server's relevance LIMIT).
      pushUrlFacets({ time: value === "all" ? null : value });
      resetVisibleCount();
    },
    [pushUrlFacets, resetVisibleCount]
  );

  const handleSortChange = useCallback(
    (value: AllStoriesSortOption) => {
      setSort(value);
      resetVisibleCount();
    },
    [resetVisibleCount]
  );

  const handleCategoryToggle = useCallback(
    ({ slug }: { slug: string }) => {
      resetVisibleCount();
      setSelectedCategorySlugs((prev) => {
        const next = prev.includes(slug)
          ? prev.filter((s) => s !== slug)
          : [...prev, slug];
        pushUrlFacets({ category: next.length > 0 ? next.join(",") : null });
        return next;
      });
    },
    [pushUrlFacets, resetVisibleCount]
  );

  const handleTagToggle = useCallback(
    ({ slug }: { slug: string }) => {
      resetVisibleCount();
      setSelectedTagSlugs((prev) => {
        const next = prev.includes(slug)
          ? prev.filter((s) => s !== slug)
          : [...prev, slug];
        // Tag filter URL: write to `?tags=` (the comma-separated form the
        // server prefers); also clear the legacy `?tag=` so we don't end
        // up with both shapes set.
        pushUrlFacets({
          tags: next.length > 0 ? next.join(",") : null,
          tag: null,
        });
        return next;
      });
    },
    [pushUrlFacets, resetVisibleCount]
  );

  const handleToolToggle = useCallback(
    ({ slug }: { slug: string }) => {
      resetVisibleCount();
      setSelectedToolSlugs((prev) => {
        const next = prev.includes(slug)
          ? prev.filter((s) => s !== slug)
          : [...prev, slug];
        // Tool slugs share the discover_tags table with topic tags, so the
        // server lumps them into the same tag facet. Pushing to ?tool= keeps
        // the UI/URL distinction (so legacy single-tool deep links keep
        // working) while letting the page route merge tools into tagSlugs.
        pushUrlFacets({ tool: next.length > 0 ? next.join(",") : null });
        return next;
      });
    },
    [pushUrlFacets, resetVisibleCount]
  );

  const handleResetAll = useCallback(() => {
    setContentType("all");
    setTimeRange("all");
    setSort("newest");
    setSelectedCategorySlugs([]);
    setSelectedTagSlugs([]);
    setSelectedToolSlugs([]);
    pushUrlFacets({
      type: null,
      category: null,
      tag: null,
      tags: null,
      tool: null,
      time: null,
    });
    resetVisibleCount();
  }, [pushUrlFacets, resetVisibleCount]);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) =>
      Math.min(prev + DEFAULT_PAGE_SIZE, filteredStories.length)
    );
  }, [filteredStories.length]);

  // ── Filtered feed data (when feed mode is active) ─────────
  const filteredFeedData = useMemo(() => {
    if (!feedData) {
      return null;
    }

    const categorySet =
      selectedCategorySlugs.length > 0 ? new Set(selectedCategorySlugs) : null;
    const tagSet =
      selectedTagSlugs.length > 0 ? new Set(selectedTagSlugs) : null;
    const toolSet =
      selectedToolSlugs.length > 0 ? new Set(selectedToolSlugs) : null;

    const matchesSlugs = (allSlugs: string[]) => {
      if (categorySet || tagSet || toolSet) {
        const slugMatches =
          (!tagSet || allSlugs.some((s) => tagSet.has(s))) &&
          (!toolSet || allSlugs.some((s) => toolSet.has(s)));
        return slugMatches;
      }
      return true;
    };

    const sections: FeedPageData["sections"] = [];
    for (const section of feedData.sections) {
      const sectionStories = section.stories.filter((story) => {
        if (
          categorySet &&
          !(story.categorySlug && categorySet.has(story.categorySlug))
        ) {
          return false;
        }
        return matchesSlugs(story.allTagSlugs);
      });

      const sectionBriefs = section.briefs.filter((brief) => {
        if (
          categorySet &&
          !brief.allCategorySlugs.some((s) => categorySet.has(s))
        ) {
          return false;
        }
        return matchesSlugs(brief.allTagSlugs);
      });

      if (sectionStories.length > 0 || sectionBriefs.length > 0) {
        sections.push({
          ...section,
          stories: sectionStories,
          briefs: sectionBriefs,
        });
      }
    }

    return { ...feedData, sections };
  }, [feedData, selectedCategorySlugs, selectedTagSlugs, selectedToolSlugs]);

  // ── Dynamic result description ──────────────────────────────
  const toolDisplayNames = useMemo(
    () => new Map(toolOptions.map((tool) => [tool.id, tool.label])),
    [toolOptions]
  );

  const resultDescription = useMemo(() => {
    const parts: string[] = [];
    for (const slug of selectedCategorySlugs) {
      parts.push(categoryDisplayNames.get(slug) ?? slug);
    }
    for (const slug of selectedTagSlugs) {
      parts.push(tagDisplayNames.get(slug) ?? slug);
    }
    for (const slug of selectedToolSlugs) {
      parts.push(toolDisplayNames.get(slug) ?? slug);
    }

    // Count stories + briefs when feed data is available
    const totalCount = filteredFeedData
      ? filteredFeedData.sections.reduce(
          (sum, s) => sum + s.stories.length + s.briefs.length,
          0
        )
      : filteredStories.length;

    if (parts.length === 0) {
      return `${totalCount} stories`;
    }
    return `${totalCount} results for ${parts.join(" + ")}`;
  }, [
    filteredStories.length,
    filteredFeedData,
    selectedCategorySlugs,
    selectedTagSlugs,
    selectedToolSlugs,
    categoryDisplayNames,
    tagDisplayNames,
    toolDisplayNames,
  ]);

  // Pre-build the rendered content for each mode so we can pick one with
  // simple if/else (avoids a nested ternary inside JSX).
  let content: React.ReactNode;
  if (isSearching) {
    if (searchError !== null && searchData === null) {
      // Distinguish a failed request from "no results" — different copy,
      // different remediation (NN/g: don't conflate the two).
      content = (
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-6">
          <p className="font-medium text-[1rem] text-[var(--explore-text-primary)]">
            {searchError}
          </p>
          <p className="text-[0.85rem] text-[var(--explore-text-muted)]">
            The search service couldn't be reached. Check your connection and
            retry.
          </p>
          <button
            className="rounded-full bg-[var(--explore-text-primary)] px-4 py-2 font-medium text-[0.82rem] text-[var(--explore-bg-card)] transition-opacity hover:opacity-90"
            onClick={() => {
              const current = searchQuery;
              setSearchQuery("");
              setTimeout(() => setSearchQuery(current), 0);
            }}
            type="button"
          >
            Retry
          </button>
        </div>
      );
    } else if (searchData === null) {
      // First-load skeleton, shaped to match the real result list.
      content = <SearchResultsSkeleton />;
    } else {
      content = (
        <div
          className={
            isSearchLoading
              ? "opacity-55 transition-opacity duration-200 ease-out"
              : "opacity-100 transition-opacity duration-200 ease-out"
          }
        >
          <StoriesSearchResults
            contentType={contentType}
            data={searchData}
            exploreBase={exploreBase}
            highlightQuery={searchQuery.trim()}
            onResetFilters={handleResetAll}
            selectedCategorySlugs={selectedCategorySlugs}
            selectedTagSlugs={selectedTagSlugs}
            selectedToolSlugs={selectedToolSlugs}
            timeRange={timeRange}
          />
        </div>
      );
    }
  } else if (filteredFeedData) {
    content = (
      <>
        <FeedTimeline briefCardStyle={briefCardStyle} data={filteredFeedData} />
        {filteredFeedData.sections.length === 0 && (
          <p className="py-6 text-[0.85rem] text-[var(--explore-text-muted)]">
            No stories or briefs match these filters.{" "}
            <button
              className="border-none bg-transparent p-0 font-medium text-[var(--explore-accent-teal)] underline"
              onClick={handleResetAll}
              type="button"
            >
              Clear all
            </button>
          </p>
        )}
      </>
    );
  } else {
    content = (
      <>
        {/* Mobile: V15 flat text cards */}
        <div className="flex flex-col gap-4 px-1 lg:hidden">
          {visibleStories.map((story, idx) => (
            <MobileStoryTextCard
              exploreBase={exploreBase}
              key={story.storyId}
              showDivider={idx < visibleStories.length - 1}
              story={story}
            />
          ))}
        </div>

        {/* Desktop: horizontal grid cards */}
        <div className="hidden flex-col gap-3 lg:flex">
          {visibleStories.map((story) => (
            <StoryGridCard
              exploreBase={exploreBase}
              key={story.storyId}
              story={story}
            />
          ))}
        </div>

        {visibleStories.length === 0 && !showFallback && (
          <p className="py-6 text-[0.85rem] text-[var(--explore-text-muted)]">
            No stories match these filters.{" "}
            <button
              className="border-none bg-transparent p-0 font-medium text-[var(--explore-accent-teal)] underline"
              onClick={handleResetAll}
              type="button"
            >
              Clear all
            </button>
          </p>
        )}

        {showFallback && fallbackStories.length > 0 && (
          <section
            className="mt-4 flex flex-col gap-3"
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
          >
            {visibleStories.length === 0 && (
              <p className="text-[0.85rem] text-[var(--explore-text-muted)]">
                No exact matches.{" "}
                <button
                  className="border-none bg-transparent p-0 font-medium text-[var(--explore-accent-teal)] underline"
                  onClick={handleResetAll}
                  type="button"
                >
                  Clear all filters
                </button>
              </p>
            )}
            <div className="flex items-center justify-between">
              <p className="text-[0.8rem] text-[var(--explore-text-muted)] uppercase tracking-[0.05em]">
                {fallbackLabel}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {fallbackStories.map((story) => (
                <FeaturedStoryCard
                  exploreBase={exploreBase}
                  key={story.storyId}
                  story={story}
                />
              ))}
            </div>
          </section>
        )}

        {/* Show more */}
        {hasMore && (
          <div className="flex justify-center pt-3 pb-4">
            <button
              className="rounded-full border-none bg-[var(--explore-tag-bg)] px-5 py-2 font-semibold text-[0.8rem] text-[var(--explore-tag-text)] transition-all hover:opacity-80 active:scale-[0.97]"
              onClick={handleLoadMore}
              style={{ fontFamily: "var(--homepage-sans-stack)" }}
              type="button"
            >
              Show more
              <span className="ml-1.5 font-medium text-[var(--explore-text-muted)]">
                ({filteredStories.length - clampedVisible} remaining)
              </span>
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Page-level title — sits above the sidebar and the right column,
          so the visual hierarchy is: page > [sidebar | content column].
          The right column starts slightly lower than the sidebar (it has
          its own visual offset) so search + results read as one block,
          distinct from the filter sidebar. */}
      <div className="mb-5 flex flex-wrap items-baseline gap-2">
        <h1
          className="font-bold text-[var(--explore-text-primary)]"
          style={{
            fontFamily: "var(--explore-card-title-stack)",
            fontSize: "clamp(1.5rem, 5.9vw, 1.95rem)",
            lineHeight: "1.02",
            fontWeight: 700,
          }}
        >
          All Stories
        </h1>
        {!isSearching && (
          <span className="pb-0.5 font-normal text-[0.68rem] text-[var(--explore-text-muted)] sm:text-[0.78rem]">
            {resultDescription}
          </span>
        )}
      </div>

      {/* Visually-hidden live region — announces result-count changes to
          screen readers after each completed search (NN/g + WAI-ARIA). */}
      <span aria-atomic="true" aria-live="polite" className="sr-only">
        {liveRegionMessage}
      </span>

      <Suspense fallback={null}>
        <StoriesUrlFilterSync onChange={syncFiltersFromSearchString} />
      </Suspense>

      {/* Content: sidebar + grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <StoriesFilterSidebar
          briefsForCategoryFacets={allBriefsForCategoryFacets}
          briefsForTagFacets={briefsAfterCategory}
          categoryDisplayNames={categoryDisplayNames}
          categoryEmojiBySlug={categoryEmojiBySlug}
          contentType={contentType}
          hasAnyFilter={hasAnyFilter}
          onCategoryToggle={handleCategoryToggle}
          onContentTypeChange={handleContentTypeChange}
          onResetAll={handleResetAll}
          onTagToggle={handleTagToggle}
          onToolToggle={handleToolToggle}
          selectedCategorySlugs={selectedCategorySlugs}
          selectedTagSlugs={selectedTagSlugs}
          selectedToolSlugs={selectedToolSlugs}
          storiesForCategoryFacets={storiesAfterTypeAndTime}
          storiesForTagFacets={storiesAfterCategory}
          tagDisplayNames={tagDisplayNames}
          toolOptions={toolOptions}
        />

        {/* Right column: search + chrome + results, slightly offset
            downward so it reads as a separate block from the sidebar. */}
        <div className="lg:pt-2">
          {searchEnabled && (
            <div className="mb-4">
              <ExploreSearchInput
                isLoading={isSearchLoading}
                onDebouncedChange={setSearchQuery}
                value={searchQuery}
              />
            </div>
          )}

          {/* Chrome row.
              Mobile: [Filter button]  [Sort/Time].
              Desktop: [empty]         [Sort/Time]. The filter sidebar lives
              in its own column, so the mobile sheet button is hidden ≥lg. */}
          <div
            className="mb-4 flex flex-wrap items-center gap-3"
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
          >
            <div className="lg:hidden">
              <MobileFilterSheet
                activeFilterCount={activeFilterCount}
                briefsForCategoryFacets={allBriefsForCategoryFacets}
                briefsForTagFacets={briefsAfterCategory}
                categoryDisplayNames={categoryDisplayNames}
                categoryEmojiBySlug={categoryEmojiBySlug}
                contentType={contentType}
                hasAnyFilter={hasAnyFilter}
                onCategoryToggle={handleCategoryToggle}
                onContentTypeChange={handleContentTypeChange}
                onResetAll={handleResetAll}
                onTagToggle={handleTagToggle}
                onToolToggle={handleToolToggle}
                selectedCategorySlugs={selectedCategorySlugs}
                selectedTagSlugs={selectedTagSlugs}
                selectedToolSlugs={selectedToolSlugs}
                storiesForCategoryFacets={storiesAfterTypeAndTime}
                storiesForTagFacets={storiesAfterCategory}
                tagDisplayNames={tagDisplayNames}
                toolOptions={toolOptions}
              />
            </div>
            <div className="ml-auto">
              <StoriesToolbar
                hideSort={isSearching}
                onSortChange={handleSortChange}
                onTimeRangeChange={handleTimeRangeChange}
                sort={sort}
                timeRange={timeRange}
              />
            </div>
          </div>

          {content}
        </div>
      </div>
    </>
  );
}
